import { Button, Input, Link } from "@heroui/react";
import { useState } from "react";
import { LuEye, LuEyeOff, LuMail, LuLock, LuShieldCheck } from "react-icons/lu";
import { useForm } from "react-hook-form";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemedText } from "@/components/ui";
import { useUser } from "@/context";
import { BASE_API, BASE_DOMAIN_API } from "@/constants";

const Login = () => {
    const [isVisible, setIsVisible] = useState(false);
    const toggleVisibility = () => setIsVisible(!isVisible);
    const navigate = useNavigate();
    const { loginSuccess } = useUser();

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting },
    } = useForm();

    const onSubmit = async (data) => {
        try {
            await axios.get(`${BASE_DOMAIN_API}/sanctum/csrf-cookie`, {
                withCredentials: true,
            });

            await axios.post(
                `${BASE_API}/login`,
                {
                    email: data.email,
                    password: data.password,
                    remember_me: data.remember_me,
                },
                {
                    withCredentials: true,
                },
            );

            const userResponse = await axios.get(`${BASE_API}/user`);
            const userData = userResponse.data.data;

            loginSuccess(userData);
            navigate("/");
        } catch (error) {
            if (error.response?.status === 422) {
                const apiErrors = error.response.data.errors;
                if (apiErrors.email) {
                    setError("email", {
                        type: "server",
                        message: apiErrors.email[0],
                    });
                }
                if (apiErrors.password) {
                    setError("password", {
                        type: "server",
                        message: apiErrors.password[0],
                    });
                }
            }
        }
    };

    return (
        <div className="flex flex-col items-center">
            {/* Header */}
            <div className="mb-6 flex flex-col items-center gap-3">
                <div className="bg-primary/10 dark:bg-primary/20 flex h-14 w-14 items-center justify-center rounded-xl">
                    <LuShieldCheck size={28} className="text-primary" />
                </div>
                <div className="text-center">
                    <ThemedText
                        as="h1"
                        variant="default"
                        size="xl"
                        className="font-semibold"
                    >
                        Masuk ke Akun Anda
                    </ThemedText>
                    <ThemedText variant="secondary" size="sm" className="mt-1">
                        Masukkan kredensial Anda untuk mengakses sistem
                    </ThemedText>
                </div>
            </div>

            {/* Login Card */}
            <div className="border-default-200 w-full rounded-lg border bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800">
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-4"
                >
                    <Input
                        autoFocus
                        label="Email"
                        isRequired
                        placeholder="nama@email.com"
                        variant="bordered"
                        startContent={
                            <LuMail
                                size={16}
                                className="text-default-400 pointer-events-none"
                            />
                        }
                        isInvalid={!!errors.email}
                        errorMessage={errors.email?.message}
                        {...register("email", {
                            required: "Email wajib diisi",
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Format email tidak valid",
                            },
                        })}
                    />

                    <div className="flex flex-col gap-1.5">
                        <Input
                            label="Password"
                            isRequired
                            placeholder="Masukkan password"
                            variant="bordered"
                            startContent={
                                <LuLock
                                    size={16}
                                    className="text-default-400 pointer-events-none"
                                />
                            }
                            endContent={
                                <button
                                    type="button"
                                    onClick={toggleVisibility}
                                    className="focus:outline-none"
                                >
                                    {isVisible ? (
                                        <LuEye
                                            size={16}
                                            className="text-default-400 pointer-events-none"
                                        />
                                    ) : (
                                        <LuEyeOff
                                            size={16}
                                            className="text-default-400 pointer-events-none"
                                        />
                                    )}
                                </button>
                            }
                            type={isVisible ? "text" : "password"}
                            isInvalid={!!errors.password}
                            errorMessage={errors.password?.message}
                            {...register("password", {
                                required: "Password wajib diisi",
                            })}
                        />
                        <div className="flex justify-end px-1">
                            <Link
                                className="text-small cursor-pointer"
                                size="sm"
                                color="primary"
                            >
                                Lupa password?
                            </Link>
                        </div>
                    </div>

                    <Button
                        color="primary"
                        type="submit"
                        isLoading={isSubmitting}
                        className="w-full font-semibold"
                    >
                        Masuk
                    </Button>
                </form>
            </div>

            {/* Security Notice */}
            <div className="border-default-200 mt-4 flex w-full items-start gap-3 rounded-lg border bg-white p-4 dark:border-zinc-700 dark:bg-zinc-800">
                <LuShieldCheck
                    size={18}
                    className="mt-0.5 shrink-0 text-green-600 dark:text-green-500"
                />
                <div>
                    <ThemedText
                        variant="default"
                        size="sm"
                        className="font-medium"
                    >
                        Koneksi Aman
                    </ThemedText>
                    <ThemedText variant="secondary" size="xs">
                        Sistem ini hanya untuk pengguna yang berwenang. Semua
                        aktivitas dipantau dan dicatat.
                    </ThemedText>
                </div>
            </div>

            {/* Help Link */}
            <ThemedText variant="secondary" size="xs" className="mt-4">
                Butuh bantuan?{" "}
                <Link
                    size="sm"
                    className="cursor-pointer text-xs"
                    href="https://wa.me/628116507345?text=Halo%2C%20saya%20butuh%20bantuan%20untuk%20login%20ke%20sistem%20Mansoer%20Group."
                    target="_blank"
                >
                    Hubungi IT Support
                </Link>
            </ThemedText>
        </div>
    );
};

export default Login;
